import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const cleanFilters = <Filters>(filters: {
    [key: string]: any;
}): {
    cleanFilterValues: Filters;
    shortCircuit: boolean;
} => {
    let shortCircuit = false;
    // If a list filter is empty, short circuit and return.
    Object.values(filters).forEach((f) => {
        if (Array.isArray(f) && f.length === 0) {
            shortCircuit = true;
        }
    });

    // Remove any "all" filters -> same as selecting all items
    const cleanFilterValues: any = Object.entries(filters).reduce(
        (acc, [key, value]) => {
            if (value === "all") {
                return acc;
            }
            return { ...acc, [key]: value };
        },
        {} as Filters,
    );

    return {
        cleanFilterValues,
        shortCircuit,
    };
};

/**
 * Grabs a parameter name from the URL and stores it as a state.
 * @param paramNames: list of strings with desired parameters
 */
export const useInitialSearchParams = <const T extends readonly string[]>(
    paramNames: T,
) => {
    const [urlParams, setUrlParams] = useSearchParams();
    const [initialParams] = useState(
        () =>
            Object.fromEntries(
                paramNames.map((p) => [p, urlParams.get(p) ?? undefined]),
            ) as Record<T[number], string | null>,
    );

    // Clear the `mapState` and `lat/lon` parameters from the URL
    useEffect(() => {
        for (const key of paramNames) {
            if (urlParams.get(key)) {
                setUrlParams(new URLSearchParams());
                break;
            }
        }
    }, [urlParams, setUrlParams, paramNames]);

    return initialParams;
};
